import React, { useState } from 'react';
import { Check, AlertTriangle, Minus, ChevronDown } from 'lucide-react';
import type { SerializedTransferStage } from '@platform/shared-types';
import { AGE_CHIP, OWNER_CHIP, OWNER_LABEL, ago, when } from './format';

/**
 * A transfer, drawn like an order: the main spine, a fork into lanes that run
 * at the same time, then the spine again. Renders whatever stage list it is
 * given — adding a stage is a data change in shared-types, not a change here.
 */

const LANES = [
  { id: 'card', title: 'Card processing', box: 'bg-rose-50/60 border-rose-200', bar: 'bg-rose-500', dot: 'bg-rose-500' },
  { id: 'data', title: 'Data & catalog', box: 'bg-emerald-50/60 border-emerald-200', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { id: 'devices', title: 'Devices & lease', box: 'bg-amber-50/60 border-amber-200', bar: 'bg-amber-500', dot: 'bg-amber-500' },
] as const;

const DOT: Record<SerializedTransferStage['state'], string> = {
  done: 'bg-emerald-500 text-white', active: 'bg-emerald-500 text-white ring-4 ring-emerald-500/20',
  blocked: 'bg-red-500 text-white ring-4 ring-red-500/15', todo: 'bg-stone-300 text-white', skipped: 'bg-stone-200 text-stone-400',
};

interface Props {
  stages: SerializedTransferStage[];
  panels?: Record<string, React.ReactNode>;
  canAct: boolean;
  onComplete: (stageId: string) => Promise<void>;
  onUndo: (stageId: string) => Promise<void>;
  openStageId?: string;
}

function StageRow({ s, index, last, panel, canAct, onComplete, onUndo, initiallyOpen }: {
  s: SerializedTransferStage; index: number; last: boolean; panel?: React.ReactNode; canAct: boolean;
  onComplete: Props['onComplete']; onUndo: Props['onUndo']; initiallyOpen: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen || ((s.state === 'active' || s.state === 'blocked') && !!panel));
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const expandable = !!panel || !!s.hint;
  return (
    <div className="grid grid-cols-[28px_1fr] gap-3">
      <div className="flex flex-col items-center">
        <span className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-bold ${DOT[s.state]}`}>
          {s.state === 'done' ? <Check size={13} /> : s.state === 'blocked' ? <AlertTriangle size={12} /> : s.state === 'skipped' ? <Minus size={12} /> : index + 1}
        </span>
        {!last && <span className={`w-0.5 flex-1 min-h-[14px] ${s.state === 'done' ? 'bg-emerald-500' : 'bg-stone-200'}`} />}
      </div>
      <div className="min-w-0 pb-4">
        <button type="button" disabled={!expandable} onClick={() => setOpen(!open)} className="flex w-full min-h-[44px] flex-wrap items-center gap-2 text-left disabled:cursor-default">
          <span className={`text-[13px] font-semibold ${s.state === 'skipped' ? 'text-stone-400 line-through' : 'text-stone-900'}`}>{s.label}</span>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${OWNER_CHIP[s.owner]}`}>{OWNER_LABEL[s.owner]}</span>
          {s.age && (s.state === 'active' || s.state === 'blocked') && <span className={`rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold ${AGE_CHIP[s.age]}`}>{ago(s.activeSince)}</span>}
          {s.blocksCompletion && s.state !== 'done' && <span className="rounded border border-rose-200 bg-rose-50 px-1.5 text-[10px] font-bold uppercase tracking-wide text-rose-800">blocks completion</span>}
          {s.needsAllLanes && s.state !== 'done' && <span className="rounded border border-rose-200 bg-rose-50 px-1.5 text-[10px] font-bold uppercase tracking-wide text-rose-800">needs all lanes</span>}
          {expandable && <ChevronDown size={14} className={`ml-auto text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
        </button>
        <div className="text-xs text-stone-500">{s.detail}{s.at ? ` · ${when(s.at)}` : ''}</div>
        {open && s.hint && !panel && <div className="mt-2 text-xs text-stone-500">{s.hint}</div>}
        {open && panel && <div className="mt-2 rounded-xl border border-stone-200 bg-white p-3">{panel}</div>}
        {canAct && s.completion === 'manual' && s.state !== 'skipped' && (
          <div className="mt-2">
            {s.state === 'done'
              ? <button type="button" disabled={busy} onClick={() => act(() => onUndo(s.id))} className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-xs font-semibold text-stone-700">Undo</button>
              : <button type="button" disabled={busy} onClick={() => act(() => onComplete(s.id))} className="min-h-[44px] rounded-lg bg-[#DC143C] px-3 text-xs font-semibold text-white">Mark done</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TransferStages({ stages, panels, canAct, onComplete, onUndo, openStageId }: Props) {
  const firstLaned = stages.findIndex((s) => s.lane !== 'main');
  const lastLaned = stages.length - 1 - [...stages].reverse().findIndex((s) => s.lane !== 'main');
  const before = firstLaned < 0 ? stages : stages.slice(0, firstLaned);
  const laned = firstLaned < 0 ? [] : stages.slice(firstLaned, lastLaned + 1);
  const after = firstLaned < 0 ? [] : stages.slice(lastLaned + 1);
  const row = (s: SerializedTransferStage, i: number, list: SerializedTransferStage[]) => (
    <StageRow key={s.id} s={s} index={stages.indexOf(s)} last={i === list.length - 1} panel={panels?.[s.id]} canAct={canAct} onComplete={onComplete} onUndo={onUndo} initiallyOpen={openStageId === s.id} />
  );
  return (
    <div>
      {before.map((s, i) => row(s, i, before))}
      {laned.length > 0 && (
        <div className="mb-4 ml-10 flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Runs in parallel</span>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {LANES.map((lane) => {
              const rows = laned.filter((s) => s.lane === lane.id);
              if (!rows.length) return null;
              const done = rows.filter((r) => r.state === 'done' || r.state === 'skipped').length;
              return (
                <div key={lane.id} className={`rounded-xl border p-3 pb-1 ${lane.box}`}>
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-stone-900"><i className={`h-2 w-2 rounded-full ${lane.dot}`} />{lane.title}<span className="ml-auto font-mono text-[11px] text-stone-500">{done}/{rows.length}</span></div>
                  <div className="my-2 h-1 overflow-hidden rounded bg-black/5"><i className={`block h-full ${lane.bar}`} style={{ width: `${Math.round((done / rows.length) * 100)}%` }} /></div>
                  {rows.map((s, i) => row(s, i, rows))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {after.map((s, i) => row(s, i, after))}
    </div>
  );
}
